import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Leaf, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [googleOnly, setGoogleOnly] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email, origin_url: window.location.origin });
      setGoogleOnly(!!res.data.google_only);
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-cream flex items-center justify-center" data-testid="forgot-password-page">
      <div className="w-full max-w-md px-6 py-12">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-12 h-12 bg-forest rounded-full flex items-center justify-center">
              <Leaf className="w-6 h-6 text-cream" />
            </div>
          </Link>
          <h1 className="font-playfair text-3xl font-bold text-dark" data-testid="forgot-password-title">Reset Your Password</h1>
          <p className="font-manrope text-dark/60 mt-2">We'll email you a link to choose a new one</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-forest/10 shadow-card">
          {sent ? (
            googleOnly ? (
              <div className="text-center" data-testid="forgot-password-google-only-message">
                <p className="font-manrope text-dark mb-4">
                  <span className="font-bold">{email}</span> signs in with Google — there's no password to reset.
                </p>
                <Link to="/login" className="btn-primary" data-testid="forgot-password-go-to-login">Continue with Google on Login</Link>
              </div>
            ) : (
              <div className="text-center" data-testid="forgot-password-sent-message">
                <p className="font-manrope text-dark">
                  If <span className="font-bold">{email}</span> is registered with FarmBox, a reset link is on its way. Check your inbox (and spam folder).
                </p>
              </div>
            )
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="font-manrope text-dark">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-12 bg-cream border-forest/20"
                    data-testid="forgot-password-email"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center"
                data-testid="forgot-password-submit"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center mt-6 font-manrope text-dark/60">
          <Link to="/login" className="text-forest font-medium hover:underline inline-flex items-center gap-1" data-testid="back-to-login-link">
            <ArrowLeft className="w-4 h-4" /> Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
